import { useState, useEffect } from 'react';
import { allocateUnitsToChannel } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const AllocateUnitsDialog = ({ isOpen, onOpenChange, selectedChannel, allUnits, initialSelectedUnitIds, onAllocated }) => {
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && initialSelectedUnitIds) {
      setSelectedUnits(initialSelectedUnitIds);
    }
  }, [isOpen, initialSelectedUnitIds]);

  const toggleUnitSelection = (unitId) => {
    setSelectedUnits(prev => 
      prev.includes(unitId) 
        ? prev.filter(id => id !== unitId)
        : [...prev, unitId]
    );
  };

  const handleAllocate = async () => {
    if (!selectedChannel) return;
    setSubmitting(true);
    try {
      await allocateUnitsToChannel(selectedChannel.channel_id, selectedUnits);
      toast.success('Units allocated successfully');
      onOpenChange(false);
      if (onAllocated) onAllocated();
    } catch (error) {
      console.error('Failed to allocate units:', error);
      toast.error(error.message || 'Failed to allocate units');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Allocate Units to {selectedChannel?.name}</DialogTitle>
          <DialogDescription>
            Select units to make available through this channel
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {selectedUnits.length} units selected
            </span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSelectedUnits(allUnits.map(u => u.unit_id))}
            >
              Select All
            </Button>
          </div>
          
          <ScrollArea className="h-[300px] border rounded-lg p-4">
            <div className="space-y-2">
              {allUnits.map((unit) => (
                <div 
                  key={unit.unit_id}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedUnits.includes(unit.unit_id)}
                      onCheckedChange={() => toggleUnitSelection(unit.unit_id)}
                      data-testid={`unit-checkbox-${unit.unit_id}`}
                    />
                    <div>
                      <p className="font-medium">{unit.unit_number}</p>
                      <p className="text-xs text-muted-foreground">{unit.configuration}</p>
                    </div>
                  </div>
                  <Badge className={
                    unit.status === 'available' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : unit.status === 'soft_blocked'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-rose-100 text-rose-700'
                  }>
                    {unit.status}
                  </Badge>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAllocate} disabled={submitting} data-testid="confirm-allocate-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Allocate {selectedUnits.length} Units
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
